import { useEffect, useMemo, useState } from "react";
import { api, AUD } from "../lib/api";
import { ErrorCard, parseApiError } from "../components/ErrorCard";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
    CartesianGrid, LineChart, Line, ComposedChart, Cell
} from "recharts";

const CAT_COLORS = {
    "Home": "#7B8F5C",
    "Living": "#C97B5A",
    "Vehicles": "#8C6E52",
    "Travel": "#4A6B7A",
    "Health": "#A88F6E",
    "Lifestyle": "#5F8071",
    "Uncategorised": "#B7A88F",
};

// Helpers for month range presets
function subMonths(ym, count) {
    let [y, m] = ym.split("-").map(Number);
    for (let i = 0; i < count; i++) {
        m -= 1;
        if (m < 1) { m = 12; y -= 1; }
    }
    return `${y}-${String(m).padStart(2, "0")}`;
}

export default function CashFlow() {
    const [available, setAvailable] = useState({ months: [], labels: {} });
    const [range, setRange] = useState({ from: null, to: null });
    const [data, setData] = useState(null);
    // Error surface — replaces the silent-forever "Loading…" when the API
    // calls fail (401/500/timeout) or when there are no transactions yet.
    const [error, setError] = useState(null);

    // Load available months once
    useEffect(() => {
        api.get("/cashflow/available-months").then(r => {
            setAvailable(r.data);
            const m = r.data.months;
            if (m.length > 0) {
                const to = m[m.length - 1];
                const from = m.length >= 12 ? m[m.length - 12] : m[0];
                setRange({ from, to });
            } else {
                setError("empty");
            }
        }).catch(e => setError(parseApiError(e)));
    }, []);

    // Load summary whenever range changes
    useEffect(() => {
        if (!range.from || !range.to) return;
        setError(null);
        api.get(`/cashflow/summary?from_ym=${range.from}&to_ym=${range.to}`)
           .then(r => setData(r.data))
           .catch(e => setError(parseApiError(e)));
    }, [range]);

    const setPreset = (kind) => {
        const m = available.months;
        if (m.length === 0) return;
        const to = m[m.length - 1];
        if (kind === "current") setRange({ from: to, to });
        else if (kind === "3mo") setRange({ from: subMonths(to, 2), to });
        else if (kind === "6mo") setRange({ from: subMonths(to, 5), to });
        else if (kind === "12mo") setRange({ from: subMonths(to, 11), to });
        else if (kind === "all") setRange({ from: m[0], to });
    };

    // Australian FY (1 Jul → 30 Jun) — clamped to available months so a
    // preset never picks a range with no data.
    const setFY = (fy) => {
        const from = `${fy - 1}-07`;
        const to = `${fy}-06`;
        const clampF = available.months.find(x => x >= from) || available.months[0];
        const clampT = [...available.months].reverse().find(x => x <= to) || available.months[available.months.length - 1];
        if (clampF && clampT) setRange({ from: clampF, to: clampT });
    };
    const setCY = (year) => {
        const from = `${year}-01`;
        const to = `${year}-12`;
        const clampF = available.months.find(x => x >= from) || available.months[0];
        const clampT = [...available.months].reverse().find(x => x <= to) || available.months[available.months.length - 1];
        if (clampF && clampT) setRange({ from: clampF, to: clampT });
    };
    const setSingleMonth = (ym) => setRange({ from: ym, to: ym });

    // FY / CY options derived from available data
    const fyOptions = useMemo(() => {
        const years = new Set();
        available.months.forEach(m => {
            const [y, mo] = m.split("-").map(Number);
            years.add(mo >= 7 ? y + 1 : y);  // AU FY: months >= July belong to next FY
        });
        return [...years].sort();
    }, [available]);
    const cyOptions = useMemo(() => {
        const years = new Set(available.months.map(m => Number(m.slice(0, 4))));
        return [...years].sort();
    }, [available]);

    const monthOptions = useMemo(() => available.months.map(m => ({
        value: m, label: available.labels[m] || m,
    })), [available]);

    if (error) return <ErrorCard error={error} subject="cash flow" testId="cf-error" />;

    if (!data) {
        return (
            <div className="p-8 text-sm" style={{ color: "var(--muted)" }} data-testid="cf-loading">
                Loading cash flow…
            </div>
        );
    }

    const { totals, months, category_matrix, expense_categories,
            top_categories, net_worth_trend } = data;
    const savingsPct = (totals.savings_rate * 100).toFixed(1);
    const netPositive = totals.net >= 0;

    return (
        <div data-testid="cashflow-page">
            <div className="mb-6">
                <div className="label-eyebrow mb-2">Cash Flow Centre</div>
                <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">
                    Where your money went.
                </h1>
                <p className="mt-3 text-sm max-w-2xl" style={{ color: "var(--muted)" }}>
                    Filter by any month range — data updates as you import new
                    transactions.
                </p>
            </div>

            {/* Date range controls */}
            <div className="hairline bg-white p-5 rounded mb-4 flex flex-wrap gap-4 items-end"
                 data-testid="cf-range-controls">
                <div>
                    <div className="label-eyebrow mb-2">From</div>
                    <select
                        value={range.from || ""}
                        onChange={e => setRange(r => ({ ...r, from: e.target.value }))}
                        data-testid="cf-range-from"
                        className="border border-[var(--line)] px-3 py-2 rounded text-sm">
                        {monthOptions.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <div className="label-eyebrow mb-2">To</div>
                    <select
                        value={range.to || ""}
                        onChange={e => setRange(r => ({ ...r, to: e.target.value }))}
                        data-testid="cf-range-to"
                        className="border border-[var(--line)] px-3 py-2 rounded text-sm">
                        {monthOptions.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <div className="label-eyebrow mb-2">Jump to a single month</div>
                    <select
                        value={range.from === range.to ? range.from || "" : ""}
                        onChange={e => e.target.value && setSingleMonth(e.target.value)}
                        data-testid="cf-single-month"
                        className="border border-[var(--line)] px-3 py-2 rounded text-sm">
                        <option value="">— pick month —</option>
                        {monthOptions.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </div>
                <div className="flex gap-1 flex-wrap ml-auto">
                    {[
                        { k: "current", label: "This month" },
                        { k: "3mo", label: "3M" },
                        { k: "6mo", label: "6M" },
                        { k: "12mo", label: "12M" },
                        { k: "all", label: "All" },
                    ].map(p => (
                        <button key={p.k} onClick={() => setPreset(p.k)}
                                data-testid={`cf-preset-${p.k}`}
                                className="btn-ghost text-xs">
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* FY and Calendar Year presets */}
            <div className="hairline bg-white p-4 rounded mb-8 flex flex-wrap gap-4 items-center text-sm"
                 data-testid="cf-year-presets">
                <div className="label-eyebrow">Financial Year</div>
                <div className="flex gap-1 flex-wrap">
                    {fyOptions.map(fy => (
                        <button key={`fy-${fy}`} onClick={() => setFY(fy)}
                                data-testid={`cf-fy-${fy}`}
                                className="btn-ghost text-xs">
                            FY{String(fy).slice(-2)}
                        </button>
                    ))}
                </div>
                <div className="w-px h-6 bg-[var(--line)]" />
                <div className="label-eyebrow">Calendar Year</div>
                <div className="flex gap-1 flex-wrap">
                    {cyOptions.map(cy => (
                        <button key={`cy-${cy}`} onClick={() => setCY(cy)}
                                data-testid={`cf-cy-${cy}`}
                                className="btn-ghost text-xs">
                            {cy}
                        </button>
                    ))}
                </div>
            </div>

            {/* KPI cards */}
            <div className="grid md:grid-cols-4 gap-6 mb-10">
                <div className="hairline bg-white p-6 rounded" data-testid="cf-kpi-income">
                    <div className="label-eyebrow">Total Income</div>
                    <div className="font-serif text-3xl mt-3 font-mono-num"
                         style={{ color: "var(--eucalyptus)" }}>
                        {AUD(totals.income)}
                    </div>
                </div>
                <div className="hairline bg-white p-6 rounded" data-testid="cf-kpi-expense">
                    <div className="label-eyebrow">Total Expenses</div>
                    <div className="font-serif text-3xl mt-3 font-mono-num"
                         style={{ color: "var(--terracotta)" }}>
                        {AUD(totals.expense)}
                    </div>
                </div>
                <div className="hairline bg-white p-6 rounded" data-testid="cf-kpi-net">
                    <div className="label-eyebrow">Net Savings</div>
                    <div className="font-serif text-3xl mt-3 font-mono-num"
                         style={{ color: netPositive ? "var(--eucalyptus)" : "var(--terracotta)" }}>
                        {AUD(totals.net)}
                    </div>
                </div>
                <div className="p-6 rounded" data-testid="cf-kpi-rate"
                     style={{ background: "var(--eucalyptus)", color: "var(--bone)" }}>
                    <div className="label-eyebrow" style={{ color: "rgba(255,255,255,0.75)" }}>
                        Savings Rate
                    </div>
                    <div className="font-serif text-3xl mt-3 font-mono-num">{savingsPct}%</div>
                    <div className="text-xs mt-2 opacity-80">
                        {data.range?.months} month{data.range?.months !== 1 ? "s" : ""}
                    </div>
                </div>
            </div>

            {/* Income vs Expense monthly */}
            <div className="hairline bg-white p-8 rounded mb-10" data-testid="cf-monthly-chart">
                <div className="flex items-baseline justify-between mb-6">
                    <div>
                        <div className="label-eyebrow">Money In vs Money Out</div>
                        <h2 className="font-serif text-2xl mt-1">Monthly income &amp; expense</h2>
                    </div>
                    <div className="text-xs" style={{ color: "var(--muted)" }}>
                        Line: net cash flow
                    </div>
                </div>
                <div style={{ height: 340 }}>
                    <ResponsiveContainer>
                        <ComposedChart data={months}>
                            <CartesianGrid stroke="var(--line)" vertical={false} />
                            <XAxis dataKey="label" stroke="var(--muted)" tick={{ fontSize: 11 }} />
                            <YAxis stroke="var(--muted)" tick={{ fontSize: 11 }}
                                   tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                            <Tooltip formatter={(v) => AUD(v)} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Bar dataKey="income" fill="var(--eucalyptus)" name="Income" radius={[3, 3, 0, 0]} />
                            <Bar dataKey="expense" fill="var(--terracotta)" name="Expense" radius={[3, 3, 0, 0]} />
                            <Line type="monotone" dataKey="net" name="Net" stroke="#3D5B54" strokeWidth={2} dot={{ r: 3 }} />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Category breakdown + Top spend */}
            <div className="grid lg:grid-cols-3 gap-6 mb-10">
                <div className="hairline bg-white p-8 rounded lg:col-span-2" data-testid="cf-category-chart">
                    <div className="mb-6">
                        <div className="label-eyebrow">Expense breakdown</div>
                        <h2 className="font-serif text-2xl mt-1">By category, month by month</h2>
                    </div>
                    <div style={{ height: 320 }}>
                        <ResponsiveContainer>
                            <BarChart data={category_matrix}>
                                <CartesianGrid stroke="var(--line)" vertical={false} />
                                <XAxis dataKey="label" stroke="var(--muted)" tick={{ fontSize: 11 }} />
                                <YAxis stroke="var(--muted)" tick={{ fontSize: 11 }}
                                       tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                                <Tooltip formatter={(v) => AUD(v)} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                {expense_categories.map(c => (
                                    <Bar key={c} dataKey={c} stackId="e" fill={CAT_COLORS[c] || "#8C6E52"} />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="hairline bg-white p-8 rounded" data-testid="cf-top-categories">
                    <div className="mb-4">
                        <div className="label-eyebrow">Top spending</div>
                        <h2 className="font-serif text-2xl mt-1">Leaders</h2>
                    </div>
                    <ul className="divide-y" style={{ borderColor: "var(--line)" }}>
                        {top_categories.map((c, i) => {
                            const pct = totals.expense > 0
                                ? ((c.total / totals.expense) * 100).toFixed(1) : "0.0";
                            return (
                                <li key={c.category} className="py-3" data-testid={`cf-top-cat-${i}`}>
                                    <div className="flex items-baseline justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className="inline-block w-2 h-2 rounded-full"
                                                  style={{ background: CAT_COLORS[c.category] || "#8C6E52" }} />
                                            <span className="text-sm font-medium">{c.category}</span>
                                        </div>
                                        <span className="font-mono-num text-sm">{AUD(c.total)}</span>
                                    </div>
                                    <div className="mt-1 h-1 rounded" style={{ background: "var(--line)" }}>
                                        <div className="h-1 rounded"
                                             style={{ width: `${pct}%`,
                                                      background: CAT_COLORS[c.category] || "#8C6E52" }} />
                                    </div>
                                    <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>
                                        {pct}% of expenses
                                    </div>
                                </li>
                            );
                        })}
                        {top_categories.length === 0 && (
                            <li className="py-6 text-sm" style={{ color: "var(--muted)" }}>
                                No expense data in this range.
                            </li>
                        )}
                    </ul>
                </div>
            </div>

            {/* Net worth trend + Savings rate */}
            <div className="grid lg:grid-cols-2 gap-6 mb-10">
                <div className="hairline bg-white p-8 rounded" data-testid="cf-net-worth-trend">
                    <div className="mb-4">
                        <div className="label-eyebrow">Net worth</div>
                        <h2 className="font-serif text-2xl mt-1">Estimated trajectory</h2>
                        <p className="text-xs mt-2" style={{ color: "var(--muted)" }}>
                            Approximated from current balance sheet less cumulative net savings.
                        </p>
                    </div>
                    <div style={{ height: 260 }}>
                        <ResponsiveContainer>
                            <LineChart data={net_worth_trend}>
                                <CartesianGrid stroke="var(--line)" vertical={false} />
                                <XAxis dataKey="label" stroke="var(--muted)" tick={{ fontSize: 11 }} />
                                <YAxis stroke="var(--muted)" tick={{ fontSize: 11 }}
                                       domain={["auto", "auto"]}
                                       tickFormatter={(v) => `$${(v / 1000000).toFixed(1)}M`} />
                                <Tooltip formatter={(v) => AUD(v)} />
                                <Line type="monotone" dataKey="net_worth" name="Net worth"
                                      stroke="var(--eucalyptus)" strokeWidth={2.5} dot={{ r: 3 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="hairline bg-white p-8 rounded" data-testid="cf-savings-rate">
                    <div className="mb-4">
                        <div className="label-eyebrow">Savings rate</div>
                        <h2 className="font-serif text-2xl mt-1">Month by month</h2>
                    </div>
                    <div style={{ height: 260 }}>
                        <ResponsiveContainer>
                            <BarChart data={months}>
                                <CartesianGrid stroke="var(--line)" vertical={false} />
                                <XAxis dataKey="label" stroke="var(--muted)" tick={{ fontSize: 11 }} />
                                <YAxis stroke="var(--muted)" tick={{ fontSize: 11 }}
                                       tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} />
                                <Tooltip formatter={(v) => `${(v * 100).toFixed(1)}%`} />
                                <Bar dataKey="savings_rate" name="Savings rate" radius={[3, 3, 0, 0]}>
                                    {months.map((m, i) => (
                                        <Cell key={i}
                                              fill={m.savings_rate >= 0
                                                  ? "var(--eucalyptus)" : "var(--terracotta)"} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Monthly detail table */}
            <div className="hairline bg-white p-8 rounded" data-testid="cf-monthly-table">
                <div className="mb-4">
                    <div className="label-eyebrow">Detail</div>
                    <h2 className="font-serif text-2xl mt-1">Monthly summary</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left" style={{ color: "var(--muted)" }}>
                                <th className="py-2 pr-4 font-normal">Month</th>
                                <th className="py-2 pr-4 font-normal text-right">Income</th>
                                <th className="py-2 pr-4 font-normal text-right">Expense</th>
                                <th className="py-2 pr-4 font-normal text-right">Net</th>
                                <th className="py-2 pr-4 font-normal text-right">Savings %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {months.map(m => (
                                <tr key={m.month} className="border-t"
                                    style={{ borderColor: "var(--line)" }}
                                    data-testid={`cf-row-${m.month}`}>
                                    <td className="py-2 pr-4">{m.label}</td>
                                    <td className="py-2 pr-4 text-right font-mono-num">{AUD(m.income)}</td>
                                    <td className="py-2 pr-4 text-right font-mono-num">{AUD(m.expense)}</td>
                                    <td className="py-2 pr-4 text-right font-mono-num"
                                        style={{ color: m.net >= 0 ? "var(--eucalyptus)" : "var(--terracotta)" }}>
                                        {AUD(m.net)}
                                    </td>
                                    <td className="py-2 pr-4 text-right font-mono-num">
                                        {(m.savings_rate * 100).toFixed(1)}%
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
